import { useMemo, useState } from "react";
import ModalShell from "../../modal/ModalShell";
import { Select, PersonOption } from "../../ui";
import ContactsTable from "../../../_features/proposals/components/ContactsTable";
import { useToast } from "../../../_context/ToastContext/ToastContext";
import { useGetAllCompanyContactsByIdQuery } from "../../../_features/company/api/companyDetailsApi";
import { useAddContactByCustomerMutation } from "../../../_features/company/api/customerApiSlice";
import { openModal } from "../../../_app/entityModals/store";
import "./_contacts-picker-modal.scss";

/**
 * Generic "pick some of this company's contacts" modal - search/select/
 * toggle, create a new contact inline via the shared entity-modal opener
 * (`openModal("create_contact")`, see `_app/entityModals`), confirm a
 * selection. Moved from `_features/proposals/modals/
 * SelectProposalContactsModal` and generalized with a `context` prop (was
 * hardcoded `context: "proposal"` on the contacts query) since Invoices
 * needs the identical picker for both its create-time "send to" field and
 * its post-creation "resend" action - one shared component instead of a
 * second near-duplicate (see docs/external-dependency-audit.md).
 *
 * `onSubmit` replaces the caller's contact list wholesale (it reports the
 * FULL selected set on confirm) - not an incremental add.
 */
const ContactsPickerModal = ({
  onClose,
  onSubmit,
  companyData,
  context,
  existingRecipients = [],
  isLoading = false,
  cardText,
  title = "Select contacts",
  submitLabel = "Save contacts",
}) => {
  const { showToast } = useToast();
  const companyId = companyData?.company?._id;

  const { data: allContactsResponse, refetch: refetchAllContacts } =
    useGetAllCompanyContactsByIdQuery(
      { id: companyId, context },
      { skip: !companyId }
    );
  // Memoized so an empty-response fallback (`|| []`) isn't a fresh array
  // reference every render - `contactOptions`/`contactsById` below depend on it.
  const allContacts = useMemo(() => allContactsResponse?.data || [], [allContactsResponse]);
  // Select's isMulti value/onChange work in ids, not full contact objects
  // (see ui/Select's own doc comment: option.value stays a plain id on
  // purpose) - this is how those ids get resolved back to the full contact
  // records `selectedContacts`/`onSubmit`/`ContactsTable` all expect.
  const contactsById = useMemo(
    () => Object.fromEntries(allContacts.map((contact) => [contact._id, contact])),
    [allContacts]
  );

  const [addContactToCustomer] = useAddContactByCustomerMutation();

  // Lazy init off the caller's current recipient list - a plain useState
  // initializer instead of a useEffect-driven sync, since every host
  // already passes this synchronously at mount, so there's no
  // later-arriving value to react to.
  const [selectedContacts, setSelectedContacts] = useState(existingRecipients);

  const contactOptions = useMemo(
    () =>
      allContacts.map((contact) => ({
        value: contact._id,
        label: contact.contactName,
        searchText: contact.contactName,
        contact,
      })),
    [allContacts]
  );

  const handleSelectionChange = (ids) => {
    setSelectedContacts(ids.map((id) => contactsById[id]).filter(Boolean));
  };

  const handleDeselectContact = (contactId) => {
    setSelectedContacts((prev) => prev.filter((c) => c._id !== contactId));
  };

  const handleOpenCreateContact = async () => {
    const contact = await openModal("create_contact", { modalProps: { companyId } });
    if (!contact) return;
    try {
      const allContactIds = allContacts.map((c) => c._id);
      await addContactToCustomer({
        id: companyId,
        data: { contactIds: [...allContactIds, contact._id] },
        context,
      }).unwrap();
      refetchAllContacts();
      setSelectedContacts((prev) => [...prev, contact]);
    } catch (error) {
      showToast(
        error?.data?.message || "Couldn't add the new contact to this company.",
        "error"
      );
    }
  };

  return (
    <ModalShell
      isOpen
      onRequestClose={onClose}
      title={title}
      width="560px"
      isSubmitting={isLoading}
      footerButtons={[
        { children: "Cancel", variant: "outlined", onClick: onClose },
        {
          children: submitLabel,
          variant: "contained",
          onClick: () => onSubmit(selectedContacts),
          loading: isLoading,
        },
      ]}
    >
      <div className="contacts-picker-modal">
        <p className="contacts-picker-modal__hint">
          {cardText ||
            `Select contacts from ${
              companyData?.company?.companyName || "this company"
            } to send to`}
        </p>

        <Select
          isMulti
          searchable
          placeholder="Search contacts"
          emptyMessage="No contacts found"
          value={selectedContacts.map((contact) => contact._id)}
          onChange={handleSelectionChange}
          options={contactOptions}
          disabled={isLoading}
          renderOption={(option) => (
            <PersonOption
              name={option.contact.contactName}
              email={option.contact.designation}
              className="contacts-picker-modal__option-person"
            />
          )}
        />

        <div className="contacts-picker-modal__selected">
          <ContactsTable
            title={`Selected contacts (${selectedContacts.length})`}
            contacts={selectedContacts}
            onRemove={handleDeselectContact}
            onAdd={handleOpenCreateContact}
            addLabel="Create new contact"
            disabled={isLoading}
          />
        </div>
      </div>
    </ModalShell>
  );
};

export default ContactsPickerModal;
